import React, { useCallback, useMemo, useState } from 'react';
import {
  FieldContext,
  FilterType,
  IEventInput,
  IFilterFieldsFragment,
  ILogicalOperator,
  INamedFilterFieldsFragment,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import SaveNamedFilterModal from './SaveNamedFilterModal';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../pages/PermissionControls/PermissionElementWrapper';
import { saveNamedFilterTooltip } from '../KubitTooltip/globalTooltips';
import FilterWidget from '../FilterWidget';
import { IPredicateContext } from '../FilterWidget/types';
import { defaultEventInput } from '../../utils/builder';
import { useHighlightingContext } from '../HighlightingContext';
import FilterFieldsView from './FilterFieldsView';
import Popover from '../Popover';
import { useActionsVisibilityContext } from '../ActionsVisibilityContext';
import { IRowMeta } from '../../redux/slices/oldBuilder';
import usePopoverController from '../Popover/usePopoverController';
import { SingleSelectOption } from '../Dropdown/SingleSelect';

interface IProps {
  rowMeta?: IRowMeta;
  schemaId: string;
  defaultLogOps: ILogicalOperator[];
  onSubmit: (newFilter: IFilterFieldsFragment) => void;
  onCreateNamedFilter: (namedFilter: INamedFilterFieldsFragment) => void;
  onExpandNamedFilter?: () => void;
  onRemove: () => void;
  filter: IFilterFieldsFragment;
  eventsInput?: IEventInput[];
  maxConditions?: number;
  maxPredicates?: number;
}

const FilterFields = ({
  rowMeta,
  schemaId,
  defaultLogOps,
  onSubmit,
  onCreateNamedFilter,
  onExpandNamedFilter,
  onRemove,
  filter,
  eventsInput,
  maxConditions,
  maxPredicates,
}: IProps) => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const {
    activateHighlighting,
    deactivateHighlighting,
    clearHighlightedByTimeout,
  } = useHighlightingContext();

  const { hideActions } = useActionsVisibilityContext();

  const { checkIsPermitted } = usePermission();
  const isPermittedToOpenSaveModal = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.FilterCreate,
      PermissionKey.FilterEdit,
    ],
    validateKind: 'OR',
    schemaId,
  });

  const handleSaveNamedFilter = useCallback(() => {
    setIsSaveModalOpen(true);

    deactivateHighlighting();
    hideActions();
  }, [deactivateHighlighting, hideActions]);

  const handleSubmitNewFilters = (newFilter: IFilterFieldsFragment) => {
    onSubmit(newFilter);

    closePopover();
  };

  const handleEditFilterClick = () => {
    openPopover();

    deactivateHighlighting();
    hideActions();
  };

  const handleSubmitNamedFilter = (namedFilter: INamedFilterFieldsFragment) => {
    onCreateNamedFilter(namedFilter);

    setIsSaveModalOpen(false);

    activateHighlighting();
    clearHighlightedByTimeout();
  };

  const allowSaveFilter = filter.type === FilterType.Basic;

  const predicateContext: IPredicateContext = {
    fieldContext: FieldContext.Filter,
    events: eventsInput ?? [defaultEventInput],
    func: null,
  };

  const extraFieldActions = useMemo(() => {
    if (allowSaveFilter) {
      return (
        <PermissionElementWrapper isPermitted={isPermittedToOpenSaveModal}>
          <SingleSelectOption
            label={'Save Filter'}
            onClick={handleSaveNamedFilter}
            tooltipOption={saveNamedFilterTooltip}
            disabled={!isPermittedToOpenSaveModal}
            onMouseEnter={activateHighlighting}
            onMouseLeave={deactivateHighlighting}
          />
        </PermissionElementWrapper>
      );
    }
  }, [
    activateHighlighting,
    allowSaveFilter,
    deactivateHighlighting,
    handleSaveNamedFilter,
    isPermittedToOpenSaveModal,
  ]);

  return (
    <>
      <FilterFieldsView
        ref={popoverAnchor}
        rowMeta={rowMeta}
        filter={filter}
        onEdit={handleEditFilterClick}
        onRemove={onRemove}
        extraActions={extraFieldActions}
        onExpandNamedFilter={onExpandNamedFilter}
        schemaId={schemaId}
      />
      {isSaveModalOpen && (
        <SaveNamedFilterModal
          onSubmit={handleSubmitNamedFilter}
          onClose={() => setIsSaveModalOpen(false)}
          filter={filter}
          schemaId={schemaId}
        />
      )}
      {isPopoverOpen && (
        <Popover anchor={popoverAnchor.current} isOpen close={closePopover}>
          <FilterWidget
            onSubmit={handleSubmitNewFilters}
            schemaId={schemaId}
            context={predicateContext}
            defaultFilter={filter}
            maxConditions={maxConditions}
            maxPredicates={maxPredicates}
            defaultLogOps={defaultLogOps}
          />
        </Popover>
      )}
    </>
  );
};

export default FilterFields;
